import React from 'react'

import countries from 'country-list'
import getSymbolFromCurrency from 'currency-symbol-map'
import { isBefore, isWithinInterval } from 'date-fns'
import format from 'date-fns/format'
import parseISO from 'date-fns/parseISO'
import { isEmpty } from 'lodash'
import orderBy from 'lodash/orderBy'
import { FieldValues, FormState } from 'react-hook-form'
import { createSearchParams, URLSearchParamsInit } from 'react-router-dom'

import { HorizontalBarItemData } from '../components/HorizontalMultiBar'
import { BarParameters, ProbableBarData } from '../components/ReportsWidgets/types'
import { CommonTableColumnsEnum } from '../components/Table/types'
import { DEFAULT_PAGE_SIZE, TRADING_TABLE_COLUMNS_ORDER, TRIGGERED_AUTO_UPLOAD } from '../constants'
import i18n from '../i18n'
import { WebinarTime } from '../pages/Webinars/types'
import { ROOT_PATHS } from '../routes/paths'
import { CountryOption, SortDirection, SortDirectionCapital } from '../types'

export const focusKeyPressNext = <T extends FieldValues>(
  e: React.KeyboardEvent,
  formState?: FormState<T>,
): void => {
  if (e.key === 'Enter') {
    if (
      formState &&
      (!formState.isValid ||
        !formState.isDirty ||
        (formState.isDirty && !isEmpty(formState.errors)))
    ) {
      e.preventDefault()
    }
    if (!formState) {
      e.preventDefault()
    }

    const forNextFocus = document.getElementById('forNextFocus')
    const inputs: HTMLInputElement[] = Array.from(
      forNextFocus?.querySelectorAll('input, select, textarea') ?? [],
    )
    const newArrayInputs = inputs.filter((item) => item.id !== 'prefixCode')
    const selectedInput = document.activeElement
    const indexOf = newArrayInputs.findIndex((item) => item.id === selectedInput?.id)
    if (indexOf + 1 < newArrayInputs.length) newArrayInputs[indexOf + 1].focus()
  }
}

export const getInitialTableColumns = () => {
  const tradingLogColumnsOrder = localStorage.getItem(TRADING_TABLE_COLUMNS_ORDER)
  let tradingLogColumns
  if (tradingLogColumnsOrder) {
    tradingLogColumns = JSON.parse(tradingLogColumnsOrder)
  } else {
    tradingLogColumns = availableTableColumns.tradingLog
  }

  return {
    tradingLog: tradingLogColumns,
    openPositions: [
      CommonTableColumnsEnum.openTime,
      CommonTableColumnsEnum.symbol,
      CommonTableColumnsEnum.volume,
      CommonTableColumnsEnum.posValue,
    ],
    closedPositions: [
      CommonTableColumnsEnum.openTime,
      CommonTableColumnsEnum.closeTime,
      CommonTableColumnsEnum.symbol,
      CommonTableColumnsEnum.volume,
      CommonTableColumnsEnum.posValue,
    ],
    cashFlows: [
      CommonTableColumnsEnum.amount,
      CommonTableColumnsEnum.cashFlowType,
      CommonTableColumnsEnum.comment,
      CommonTableColumnsEnum.date,
    ],
    logHistory: [
      CommonTableColumnsEnum.date,
      CommonTableColumnsEnum.action,
      CommonTableColumnsEnum.account,
      CommonTableColumnsEnum.comment,
    ],
    importHistory: [
      CommonTableColumnsEnum.date,
      CommonTableColumnsEnum.action,
      CommonTableColumnsEnum.account,
      CommonTableColumnsEnum.status,
      CommonTableColumnsEnum.comment,
    ],
  }
}

export const availableTableColumns = {
  tradingLog: [
    CommonTableColumnsEnum.ticket,
    CommonTableColumnsEnum.openTime,
    CommonTableColumnsEnum.closeTime,
    CommonTableColumnsEnum.symbol,
    CommonTableColumnsEnum.status,
    CommonTableColumnsEnum.type,
    CommonTableColumnsEnum.volume,
    CommonTableColumnsEnum.lots,
    CommonTableColumnsEnum.openPrice,
    CommonTableColumnsEnum.closePrice,
    CommonTableColumnsEnum.grossProfit,
    CommonTableColumnsEnum.netProfit,
    CommonTableColumnsEnum.roi,
    CommonTableColumnsEnum.pips,
    CommonTableColumnsEnum.journalingCompletion,
    CommonTableColumnsEnum.riskLevel,
    CommonTableColumnsEnum.commissions,
    CommonTableColumnsEnum.fee,
    CommonTableColumnsEnum.swap,
  ],
}

export const getInitialPaginationData = <T, F = []>(columns: (keyof T)[], filters?: F) => {
  return {
    perPage: DEFAULT_PAGE_SIZE,
    period: [null, null],
    page: 1,
    columns,
    itemCount: 0,
    pageCount: 0,
    hasPreviousPage: false,
    hasNextPage: false,
    account: 0,
    order: SortDirectionCapital.asc,
    orderBy: null,
    fromCalendar: false,
    search: '',
    filters: filters ?? ([] as F),
  }
}

export const getInitialPaginationDataFromKeys = <T, F = []>(columns: T[], filters?: F) => {
  return {
    perPage: DEFAULT_PAGE_SIZE,
    period: [null, null],
    page: 1,
    columns,
    itemCount: 0,
    pageCount: 0,
    hasPreviousPage: false,
    hasNextPage: false,
    account: 0,
    order: SortDirectionCapital.asc,
    orderBy: null,
    fromCalendar: false,
    search: '',
    filters: filters ?? ([] as F),
  }
}

export const currencyFormatter = (
  currency: string | null | undefined,
  value: number,
  options: Intl.NumberFormatOptions = {},
  roundToInteger = false,
) => {
  const symbol = currency ? getSymbolFromCurrency(currency) : ''
  const formattedValue =
    value || value === 0
      ? Intl.NumberFormat('en-US', {
          style: 'decimal',
          minimumFractionDigits: roundToInteger ? 0 : 2,
          maximumFractionDigits: roundToInteger ? 0 : 2,
          ...options,
        }).format(Math.abs(value))
      : '-'

  // symbol or currency if currency is crypto
  return `${value < 0 ? '-' : ''}${symbol || `${currency || ''} `}${formattedValue}`
}

export const countryOptions: CountryOption[] = countries.getData().map((country) => ({
  label: country.name,
  code: country.code,
}))

export const getCountryByName = (countryName: string) =>
  countryOptions.find(({ label }) => label.toLowerCase() === countryName.toLowerCase())

export const isDateInRange = (selectedDate: Date, startDate: Date, endDate: Date) => {
  return isWithinInterval(selectedDate, { start: startDate, end: endDate })
}

export const getNormalizedBarData = <T extends BarParameters>(
  data: ProbableBarData<T>[],
  param: T,
  sortKey: keyof Omit<ProbableBarData<T>, T>,
  maxItemsPerWidget: number,
): HorizontalBarItemData[] => {
  if (!data) return []
  const normalizedValue = data.map((item) => ({ ...item, [sortKey]: Number(item[sortKey]) }))
  const sortedData = orderBy(normalizedValue, sortKey, SortDirection.desc)
  const slicedData = sortedData.slice(0, maxItemsPerWidget)
  return slicedData.map((item) => ({
    parameter:
      // param === 'symbol' ? item[param].toUpperCase().replace(/(.{3})/, '$1/') : item[param],
      param === 'symbol' ? item[param].toUpperCase() : item[param],
    value: item[sortKey],
  }))
}

export const calcSqnLevel = (sqn: number | null) => {
  const t = i18n.t

  if (sqn === null)
    return t('notEnoughNumberOfTradesToCalculate', 'Not enough number of trades to calculate')

  if (sqn > 0 && sqn <= 1.5) return t('probablyVeryHardToTrade', 'Probably very hard to trade')
  if (sqn > 1.5 && sqn <= 2) return t('averageToTrade', 'Average')
  if (sqn > 2 && sqn <= 3) return t('goodToTrade', 'Good')
  if (sqn > 3 && sqn <= 5) return t('excellentToTrade', 'Excellent')
  if (sqn > 5 && sqn <= 7) return t('superToTrade', 'Super')
  if (sqn > 7) return t('holyGrailToTrade', 'Holy Grail')
}

export const generateAutoUploadLSKey = (accountId: number) => TRIGGERED_AUTO_UPLOAD + accountId

export const checkMobile = () => {
  const hasCoarsePointer = () => window.matchMedia('(pointer: coarse)').matches
  const hasMobileWidth = (maxWidth = 1000) =>
    window.matchMedia(`(max-width: ${maxWidth}px)`).matches
  const hasMultipleTouchPoints = () => navigator.maxTouchPoints > 1
  const hasTouchEvents = () => 'ontouchstart' in document.documentElement

  return hasCoarsePointer() && hasMultipleTouchPoints() && hasMobileWidth() && hasTouchEvents()
}

export const isEducationPage = (path: string) => path.includes('education')

export const isWebinarsPage = (path: string) => path.includes('webinars')

export const isTradingPlanPage = (path: string) => path.includes('trading-plan')

export const getCompletedModulePath = (moduleId: number) =>
  `${ROOT_PATHS.completedModule}/${moduleId}`

export const getCompletedQuizPath = (programId: number) =>
  `${ROOT_PATHS.completedQuiz}/${programId}`

export const getNotCompletedQuizPath = (programId: number) =>
  `${ROOT_PATHS.notCompletedQuiz}/${programId}`

export const generateSearchPath = (path: string, params: URLSearchParamsInit) => {
  return `${path}?${createSearchParams(params)}`
}

export const stringifyParams = <T>(inputObject: T): Record<keyof T, string> => {
  const result: Record<keyof T, string> = {} as Record<keyof T, string>

  for (const key in inputObject) {
    if (Object.hasOwnProperty.call(inputObject, key)) {
      // eslint-disable-next-line  @typescript-eslint/no-non-null-assertion
      result[key] = inputObject[key]!.toString()
    }
  }

  return result
}

export const calculateTimeDifference = (targetDate: string) => {
  const lastQuizDate = new Date(targetDate)
  const endDate = new Date(lastQuizDate.setDate(lastQuizDate.getDate() + 1))
  const nowDate = new Date()

  const timeDifference = endDate.getTime() - nowDate.getTime()

  if (timeDifference >= 0) {
    const hours = Math.floor(timeDifference / (1000 * 60 * 60))
    const minutes = Math.floor((timeDifference % (1000 * 60 * 60)) / (1000 * 60))
    return `${hours} hours and ${minutes} minutes`
  } else {
    return 'More than a day has passed'
  }
}

export const generateWebinarDate = (dates: WebinarTime) => {
  const { startTime, endTime } = dates

  return (
    format(parseISO(startTime), 'HH:mm') +
    '-' +
    format(parseISO(endTime), 'HH:mm') +
    ' • ' +
    format(parseISO(startTime), 'MMM d yyyy')
  )
}

export const calculateTimeRemaining = (targetTime: string) => {
  const now = new Date().getTime()
  const target = new Date(targetTime).getTime()
  const difference = target - now

  if (difference <= 0) {
    return {
      days: 0,
      hours: 0,
      minutes: 0,
    }
  }

  const days = Math.floor(difference / (1000 * 60 * 60 * 24))
  const hours = Math.floor((difference % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60))
  const minutes = Math.floor((difference % (1000 * 60 * 60)) / (1000 * 60))

  return {
    days,
    hours: hours < 10 ? `0${hours}` : hours,
    minutes: minutes < 10 ? `0${minutes}` : minutes,
  }
}

export const isDateWithinInterval = (dateArray: WebinarTime[]) => {
  const startDate = parseISO(dateArray[0].startTime)
  const endDate = parseISO(dateArray[0].endTime)
  return isWithinInterval(parseISO(new Date().toString()), { start: startDate, end: endDate })
}

export const checkIfWebinarUpcoming = (dateArray: WebinarTime[]) => {
  const startDate = parseISO(dateArray[0].startTime)
  const isWebinarGoing = isDateWithinInterval(dateArray)

  return isWebinarGoing || isBefore(new Date(), startDate)
}
